// The hiding (masking) rule editor (REQ-10.3) for S9's Hiding rules > Your own rules (S8 asks for no rule since
// CT-028): a page element's CSS selector, a library pattern, a custom pattern or an SDK context key. It only builds the
// rule; the caller adds it (POST .../masking-rules). A div, not a form: Enter in one of its inputs adds the rule, and
// the editor can sit inside a page's form without submitting it.

import type { MaskingRuleKind } from '@feedback/shared/constants/code-lists';
import type { CreateMaskingRuleInput } from '@feedback/shared/contracts/apps';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { referenceQuery } from '@/lib/queries';
import { fieldError } from '../lib/admin-api';
import { Button, Field, Input, Notice, Select } from '../ui/primitives';

/** The add form's kinds: a pattern rule is either a library pattern or a custom expression. */
type RuleChoice = 'page_element' | 'library_pattern' | 'custom_pattern' | 'context_key';

interface Choice {
  value: RuleChoice;
  label: string;
  targetLabel: string;
  placeholder: string;
}
const PAGE_ELEMENT: Choice = {
  value: 'page_element',
  label: 'Page element',
  targetLabel: 'CSS selector',
  placeholder: '#guest-name, .guest-email',
};
const CHOICES: Choice[] = [
  PAGE_ELEMENT,
  { value: 'library_pattern', label: 'Library pattern', targetLabel: '', placeholder: '' },
  {
    value: 'custom_pattern',
    label: 'Custom pattern',
    targetLabel: 'Regular expression',
    placeholder: '\\bBK-\\d{6}\\b',
  },
  { value: 'context_key', label: 'SDK context key', targetLabel: 'Context key', placeholder: 'guest.email' },
];

export const KIND_LABEL: Record<MaskingRuleKind, string> = {
  page_element: 'Page element',
  pattern: 'Pattern',
  context_key: 'SDK context key',
};

export function HidingRuleEditor({
  onAdd,
  busy = false,
  errors = {},
  problem,
  className,
}: {
  /** Adds the rule; true when it was added, so the fields clear for the next one. */
  onAdd(rule: CreateMaskingRuleInput): boolean | Promise<boolean>;
  busy?: boolean;
  /** The server's field errors for the rule being added (target, maskingPatternId, description). */
  errors?: Record<string, string>;
  /** A refusal with no field to show it on. */
  problem?: string;
  className?: string;
}) {
  const reference = useQuery(referenceQuery);
  const [choice, setChoice] = useState<RuleChoice>('page_element');
  const [target, setTarget] = useState('');
  const [patternId, setPatternId] = useState('');
  const [description, setDescription] = useState('');
  const meta = CHOICES.find((c) => c.value === choice) ?? PAGE_ELEMENT;

  function body(): CreateMaskingRuleInput {
    const extra = description.trim() ? { description: description.trim() } : {};
    if (choice === 'library_pattern') return { kind: 'pattern', maskingPatternId: patternId, ...extra };
    if (choice === 'custom_pattern') return { kind: 'pattern', target: target.trim(), ...extra };
    return { kind: choice, target: target.trim(), ...extra };
  }
  const ready = choice === 'library_pattern' ? Boolean(patternId) : Boolean(target.trim());

  async function add() {
    if (!ready || busy) return;
    if (await onAdd(body())) {
      setTarget('');
      setPatternId('');
      setDescription('');
    }
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: only catches Enter from the inputs inside (a form's implicit submit).
    <div
      className={className ?? 'flex flex-col gap-3'}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
          e.preventDefault();
          void add();
        }
      }}
    >
      <h3 className="text-sm font-semibold">Add a rule</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Kind">
          {({ id }) => (
            <Select id={id} value={choice} onChange={(e) => setChoice(e.target.value as RuleChoice)}>
              {CHOICES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {choice === 'library_pattern' ? (
          <Field label="Pattern" error={fieldError(errors, 'maskingPatternId')}>
            {({ id, invalid }) => (
              <Select id={id} aria-invalid={invalid} value={patternId} onChange={(e) => setPatternId(e.target.value)}>
                <option value="">Choose a pattern…</option>
                {reference.data?.maskingPatterns.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ) : (
          <Field label={meta.targetLabel} error={fieldError(errors, 'target')}>
            {({ id, invalid }) => (
              <Input
                id={id}
                aria-invalid={invalid}
                value={target}
                placeholder={meta.placeholder}
                onChange={(e) => setTarget(e.target.value)}
              />
            )}
          </Field>
        )}
      </div>
      <Field label="Description (optional)" error={fieldError(errors, 'description')}>
        {({ id }) => <Input id={id} value={description} onChange={(e) => setDescription(e.target.value)} />}
      </Field>
      {problem ? <Notice tone="danger">{problem}</Notice> : null}
      <div>
        <Button variant="primary" busy={busy} disabled={!ready} onClick={() => void add()}>
          <Plus aria-hidden="true" className="size-4" /> Add rule
        </Button>
      </div>
    </div>
  );
}
